
import { supabase } from '@/integrations/supabase/client';
import { Farm } from '@/types/database.types';

export const getFarms = async (): Promise<Farm[]> => {
  const { data, error } = await supabase
    .from('farms')
    .select('*')
    .order('farmer_name');

  if (error) {
    console.error('Error fetching farms:', error);
    throw new Error(error.message);
  }

  return data || [];
};

export const getFarmById = async (id: string): Promise<Farm | null> => {
  const { data, error } = await supabase
    .from('farms')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error(`Error fetching farm ${id}:`, error);
    throw new Error(error.message);
  }

  return data;
};

// Use the CreateFarmInput type from database.types
import { CreateFarmInput } from '@/types/database.types';

export const createFarm = async (farm: CreateFarmInput): Promise<Farm> => {
  const { data, error } = await supabase
    .from('farms')
    .insert(farm)
    .select()
    .single();

  if (error) {
    console.error('Error creating farm:', error);
    throw new Error(error.message);
  }

  return data;
};

export const getFarmsByCoordinator = async (coordinatorId: string): Promise<Farm[]> => {
  const { data, error } = await supabase
    .from('farms')
    .select('*')
    .eq('coordinator_id', coordinatorId)
    .order('farmer_name');

  if (error) {
    console.error('Error fetching farms by coordinator:', error);
    throw new Error(error.message);
  }

  return data || [];
};

export const getCoordinatorStats = async (coordinatorId: string) => {
  const { data, error } = await supabase
    .from('farms')
    .select('id, created_at, has_phone_access, has_contract')
    .eq('coordinator_id', coordinatorId);

  if (error) {
    console.error('Error fetching coordinator stats:', error);
    throw new Error(error.message);
  }

  const farms = data || [];
  return {
    totalFarms: farms.length,
    farmsWithPhoneAccess: farms.filter(f => f.has_phone_access).length,
    farmsWithContracts: farms.filter(f => f.has_contract).length,
    recentRegistrations: farms.filter(f => {
      const createdDate = new Date(f.created_at);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return createdDate >= weekAgo;
    }).length
  };
};

// For updates, we need a different type since we don't require all fields
export const updateFarm = async (id: string, farm: Partial<Farm>): Promise<Farm> => {
  const { data, error } = await supabase
    .from('farms')
    .update(farm)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error(`Error updating farm ${id}:`, error);
    throw new Error(error.message);
  }

  return data;
};

export const deleteFarm = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('farms')
    .delete()
    .eq('id', id);

  if (error) {
    console.error(`Error deleting farm ${id}:`, error);
    throw new Error(error.message);
  }
};
